import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import type { ReactNode } from "react";
import Image from "next/image";

import type { STTFloatingCardHeaderModel } from "./hooks/useSTTFloatingCardHeader";

const headerContentMotion = {
  initial: { opacity: 0, filter: "blur(5px)" },
  animate: { opacity: 1, filter: "blur(0px)" },
  exit: { opacity: 0, filter: "blur(2px)" },
  transition: { duration: 0.25, ease: "easeOut" },
} as const;

function getHeaderAnimationKey(model: STTFloatingCardHeaderModel) {
  if (model.kind === "legacy") {
    return `${model.kind}:${model.title}`;
  }

  return `${model.kind}:${model.title}:${model.fileType}:${model.durationLabel}`;
}

function renderVideoPreviewHeader({
  title,
  fileType,
  durationLabel,
  thumbnail,
}: {
  title: string;
  fileType: string;
  durationLabel: string;
  thumbnail: ReactNode;
}) {
  return (
    <div className='tw-flex tw-items-center tw-gap-3 tw-shrink-0 tw-rounded-2xl tw-border tw-border-black/10 tw-bg-gray-100 tw-p-2'>
      {thumbnail}
      <div className='tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-justify-center tw-gap-0.5 tw-overflow-hidden'>
        <p className='tw-type-sm tw-text-black tw-w-full tw-truncate tw-py-1' data-testid='stt-hero-card-title'>
          {title}
        </p>
        <p className='tw-type-xs tw-text-gray-500 tw-w-full tw-truncate tw-py-1 tw-tabular-nums'>
          {fileType} • {durationLabel}
        </p>
      </div>
    </div>
  );
}

function renderHeader(model: STTFloatingCardHeaderModel) {
  if (model.kind === "legacy") {
    return (
      <p
        className='tw-type-sm tw-text-gray-500 tw-shrink-0 tw-text-center tw-truncate tw-py-1.5'
        data-testid='stt-hero-card-title'
      >
        {model.title}
      </p>
    );
  }

  if (model.kind === "videoPreviewUpload") {
    const uploadThumbnail = model.uploadedThumbnailUrl ? (
      <div
        className='tw-relative tw-aspect-[4/3] tw-w-16 tw-shrink-0 tw-overflow-hidden tw-rounded-xl tw-bg-gray-200'
        data-testid='stt-hero-upload-thumbnail-wrap'
      >
        <Image
          src={model.uploadedThumbnailUrl}
          alt='Uploaded video thumbnail'
          fill
          unoptimized
          data-testid='stt-hero-upload-thumbnail'
          className='tw-object-cover'
        />
      </div>
    ) : (
      <div
        className='tw-aspect-[4/3] tw-w-16 tw-shrink-0 tw-rounded-xl tw-bg-gray-300'
        data-testid='stt-hero-video-preview-slot'
        aria-hidden
      />
    );

    return renderVideoPreviewHeader({
      title: model.title,
      fileType: model.fileType,
      durationLabel: model.durationLabel,
      thumbnail: uploadThumbnail,
    });
  }

  return renderVideoPreviewHeader({
    title: model.title,
    fileType: model.fileType,
    durationLabel: model.durationLabel,
    thumbnail: (
      <div
        className='tw-relative tw-aspect-[4/3] tw-w-16 tw-shrink-0 tw-overflow-hidden tw-rounded-xl tw-bg-gray-200'
        data-testid='stt-hero-sample-thumbnail-wrap'
      >
        <Image
          {...model.thumbnailProps}
          data-testid='stt-hero-sample-thumbnail'
          sizes={model.thumbnailSizes}
          className='tw-object-cover'
        />
      </div>
    ),
  });
}

export function STTFloatingCardHeader({ model }: { model: STTFloatingCardHeaderModel }) {
  return (
    <AnimatePresence mode='wait' initial={false}>
      <m.div key={getHeaderAnimationKey(model)} className='tw-shrink-0' {...headerContentMotion}>
        {renderHeader(model)}
      </m.div>
    </AnimatePresence>
  );
}
